import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import EmptyState from "../../components/ui/EmptyState";
import QueryBoundary from "../../components/ui/QueryBoundary";
import useApiQuery from "../../hooks/useApiQuery";
import useScrollTop from "../../hooks/useScrollTop";
import endpoints from "../../api/endpoints";

const CategoryCard = ({ title, image, slug }) => (
  <Link to={`/category/${slug}`}>
    <div className="relative cursor-pointer bg-gray-800 text-white">
      <img
        src={image}
        className="h-[100px] w-full rounded-md object-cover sm:h-[220px]"
        alt={title}
        loading="lazy"
      />
      <div className="absolute bottom-7 right-0 p-4">
        <h2 className="text-xl font-semibold leading-9">{title}</h2>
      </div>
    </div>
  </Link>
);

const CategoriesPage = () => {
  useScrollTop();
  const query = useApiQuery(endpoints.categories());
  const groups = useMemo(() => query.data ?? [], [query.data]);
  const [activeId, setActiveId] = useState(null);

  // Default to the "ژانر" group when present, otherwise the first one.
  useEffect(() => {
    if (groups.length === 0) return;
    const preferred = groups.find((group) => group.title === "ژانر");
    setActiveId((current) => current ?? (preferred ?? groups[0]).id);
  }, [groups]);

  const children =
    groups.find((group) => group.id === activeId)?.children ?? [];

  return (
    <QueryBoundary
      {...query}
      isEmpty={(data) => data.length === 0}
      emptyMessage="دسته‌بندی‌ای موجود نیست."
    >
      {() => (
        <>
          <div className="mx-auto mb-40 mt-[6rem] flex flex-row flex-wrap justify-center gap-4 px-4">
            {groups.map((group) => (
              <button
                key={group.id}
                type="button"
                onClick={() => setActiveId(group.id)}
                aria-pressed={group.id === activeId}
                className={`h-[2.8rem] w-[7rem] cursor-pointer rounded border border-white text-base font-semibold leading-7 text-white transition ${
                  group.id === activeId ? "bg-brand" : "hover:bg-white/10"
                }`}
              >
                {group.title}
              </button>
            ))}
          </div>

          <div className="mb-60 px-4 py-8 md:px-12">
            {children.length > 0 ? (
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
                {children.map((category) => (
                  <CategoryCard
                    key={category.id}
                    slug={category.slug}
                    title={category.title}
                    image={category.image}
                  />
                ))}
              </div>
            ) : (
              <EmptyState message="زیرمجموعه‌ای برای این دسته وجود ندارد." />
            )}
          </div>
        </>
      )}
    </QueryBoundary>
  );
};

export default CategoriesPage;
